// SubscriptionManager.js
import React, { useState, useEffect, createContext, useContext } from "react";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { X, AlertTriangle, Clock, CreditCard } from "lucide-react";
import moment from "moment";

// Context for subscription status
const SubscriptionContext = createContext();

// Provider component
export const SubscriptionProvider = ({ children, organizationId }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isExpired: false,
    isGracePeriod: false,
    isReadOnly: false,
    expirationDate: null,
    daysOverdue: 0,
    loading: true,
  });

  useEffect(() => {
    const checkSubscription = async () => {
      if (!organizationId) {
        setSubscriptionStatus((prev) => ({ ...prev, loading: false }));
        return;
      }

      try {
        const db = getFirestore();
        const houseSettingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const houseSettingsSnap = await getDoc(houseSettingsRef);

        if (!houseSettingsSnap.exists()) {
          setSubscriptionStatus((prev) => ({ ...prev, loading: false }));
          return;
        }

        const data = houseSettingsSnap.data();
        const expirationDate = data.expirationDate;

        if (!expirationDate) {
          setSubscriptionStatus((prev) => ({ ...prev, loading: false }));
          return;
        }

        // Convert Firestore timestamp to moment
        const expDate = moment(expirationDate.toDate()); // Add .toDate() here
        const today = moment();
        const daysOverdue = today.diff(expDate, "days");

        const isExpired = today.isAfter(expDate);
        const isGracePeriod = isExpired && daysOverdue <= 30;
        const isReadOnly = isExpired && daysOverdue > 30;

        setSubscriptionStatus({
          isExpired,
          isGracePeriod,
          isReadOnly,
          expirationDate: expDate.format("YYYY-MM-DD"),
          daysOverdue: Math.max(0, daysOverdue),
          loading: false,
        });
      } catch (error) {
        console.error("Error checking subscription status:", error);
        setSubscriptionStatus((prev) => ({ ...prev, loading: false }));
      }
    };

    checkSubscription();
  }, [organizationId]);

  return (
    <SubscriptionContext.Provider value={subscriptionStatus}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Hook to use subscription status
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

// Banner component
export const SubscriptionBanner = ({ onDismiss }) => {
  const subscriptionStatus = useSubscription();
  const { isExpired, isGracePeriod, isReadOnly, daysOverdue } =
    subscriptionStatus;

  if (!isExpired) return null;

  let config;
  if (isReadOnly) {
    config = {
      bgColor: "bg-red-600/90",
      icon: <AlertTriangle className="h-5 w-5" />,
      title: "Subscription Suspended",
      message: `Your subscription expired ${daysOverdue} days ago. Features are now read-only.`,
      actionText: "Reactivate Now",
      canDismiss: false,
    };
  } else if (isGracePeriod) {
    config = {
      bgColor: "bg-amber-600/90",
      icon: <Clock className="h-5 w-5" />,
      title: "Subscription Expired",
      message: `Your subscription expired ${daysOverdue} days ago. ${
        30 - daysOverdue
      } days remaining before features become read-only.`,
      actionText: "Renew Subscription",
      canDismiss: true,
    };
  }

  if (!config) return null;

  const handleRenewal = () => {
    // TODO: Add your payment/renewal logic here
    console.log("Navigate to billing/payment page");
    alert("Redirect to billing page - implement your payment flow here");
  };

  return (
    <div
      className={`${config.bgColor} backdrop-blur-sm border-b border-white/20 relative z-50`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className={`p-1 rounded-full ${
                isReadOnly ? "animate-pulse" : ""
              }`}
            >
              {config.icon}
            </div>
            <div>
              <h3 className="font-semibold text-white">{config.title}</h3>
              <p className="text-sm text-white opacity-90">{config.message}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {config.canDismiss && onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Component to block interactions when read-only
export const SubscriptionGate = ({ children, fallback = null }) => {
  const { isReadOnly } = useSubscription();

  if (isReadOnly) {
    return (
      fallback || (
        <div className="relative">
          {children}
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px] rounded-lg 
                      flex items-center justify-center cursor-not-allowed z-10"
          >
            <div className="bg-slate-800/90 px-4 py-2 rounded-lg border border-slate-700">
              <p className="text-sm text-slate-300">Subscription required</p>
            </div>
          </div>
        </div>
      )
    );
  }

  return children;
};
